import React, { useEffect, useState } from "react";
import { Modal, Input, Switch, message, Typography } from "antd";
import { createInvite } from "./controller";
import { controller as loginController } from "../../Login/controller";

const { Paragraph } = Typography;

// `initial`: {email, practiceName, contactPersonName, contactPhone} to seed the
// fields from (a lead). Applied each time the modal opens, so the same modal
// serves the header button (blank) and a lead row (prefilled, still editable).
// `leadId`: recorded on the invite so the lead reads "invited" and the
// registrant is linked back to it at signup.
export default function InviteCustomerModal({ open, onClose, onCreated, initial, leadId }) {
  const [email, setEmail] = useState("");
  const [practiceName, setPracticeName] = useState("");
  const [contactPersonName, setContactPersonName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [trialEnabled, setTrialEnabled] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  // The invite we just made with the email suppressed — shown so the admin can
  // act on it straight away instead of hunting for it in the list.
  const [createdLink, setCreatedLink] = useState(null);
  // Read the quota from the server rather than hard-coding it, so this copy
  // can't drift when the number changes.
  const [quota, setQuota] = useState(null);

  useEffect(() => {
    if (!open) return;
    const seed = initial || {};
    setEmail(seed.email || "");
    setPracticeName(seed.practiceName || "");
    setContactPersonName(seed.contactPersonName || "");
    setContactPhone(seed.contactPhone || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    loginController
      .getTrialConfig()
      .then((cfg) => {
        if (!cancelled && cfg && cfg.status < 250 && cfg.trial_request_quota != null) {
          setQuota(cfg.trial_request_quota);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open]);

  const reset = () => {
    setEmail("");
    setPracticeName("");
    setContactPersonName("");
    setContactPhone("");
    setTrialEnabled(false);
    setSendEmail(true);
    setCreatedLink(null);
  };

  const closeAll = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    if (!email || !email.trim()) {
      message.error("Enter an email address.");
      return;
    }
    setLoading(true);
    const res = await createInvite(email.trim(), trialEnabled, {
      practiceName: practiceName.trim(),
      contactPersonName: contactPersonName.trim(),
      contactPhone: contactPhone.trim(),
      sendEmail,
      leadId,
    });
    setLoading(false);
    if (res.status >= 200 && res.status < 300) {
      const body = res.json || {};
      if (onCreated) onCreated();
      if (body.email_suppressed) {
        // Keep the modal open on the link — it is the only thing the admin can
        // act on, and the customer has no idea the invite exists.
        message.success("Invite created — no email sent.");
        setCreatedLink(body.invite_link || null);
        return;
      }
      message.success(
        body.email_sent
          ? "Invite created and emailed."
          : "Invite created, but the email failed to send — copy the link from the list."
      );
      closeAll();
    } else {
      message.error((res.json && res.json.detail) || "Failed to create invite.");
    }
  };

  // --- the post-create screen for a suppressed invite ---
  if (createdLink !== null) {
    return (
      <Modal
        title="Invite created — not emailed"
        open={open}
        onCancel={closeAll}
        onOk={closeAll}
        okText="Done"
        cancelButtonProps={{ style: { display: "none" } }}
      >
        <p style={{ marginBottom: 6 }}>
          Nothing was sent to <b>{email}</b>. Use this link to complete their registration.
        </p>
        {createdLink ? (
          <Paragraph copyable={{ text: createdLink }} style={{ wordBreak: "break-all", marginBottom: 6 }}>
            {createdLink}
          </Paragraph>
        ) : (
          <p style={{ color: "#c00" }}>
            The link didn't come back — copy it from the invites list instead.
          </p>
        )}
        <div style={{ fontSize: 12, color: "#888" }}>
          It's a one-time code, so once it's used it can't be reused. You can also copy it
          again later from the invites list.
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={leadId ? "Invite this lead to register" : "Invite a customer to register"}
      open={open}
      onCancel={closeAll}
      onOk={submit}
      confirmLoading={loading}
      okText={sendEmail ? "Send invite" : "Create invite"}
    >
      <p>
        {sendEmail
          ? "They'll receive a registration link with a one-time invite code."
          : "An invite is created with a one-time code, but nothing is emailed — you'll get the link to use yourself."}
      </p>
      <Input
        type="email"
        placeholder="customer@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onPressEnter={submit}
      />
      <div style={{ marginTop: 12, color: "#888", fontSize: 12 }}>Optional details (prefill their practice):</div>
      <Input
        style={{ marginTop: 6 }}
        placeholder="Practice name"
        value={practiceName}
        onChange={(e) => setPracticeName(e.target.value)}
      />
      <Input
        style={{ marginTop: 8 }}
        placeholder="Contact person name"
        value={contactPersonName}
        onChange={(e) => setContactPersonName(e.target.value)}
      />
      <Input
        style={{ marginTop: 8 }}
        placeholder="Contact phone"
        value={contactPhone}
        onChange={(e) => setContactPhone(e.target.value)}
      />

      <div style={{ marginTop: 18, display: "flex", alignItems: "flex-start", gap: 12 }}>
        <Switch checked={trialEnabled} onChange={setTrialEnabled} />
        <div>
          <div style={{ fontWeight: 500 }}>Offer a free trial</div>
          <div style={{ fontSize: 12, color: "#888" }}>
            If on, this customer gets {quota != null ? quota : 25} free breakdown and estimate
            requests before they have to pick a plan. If off, they subscribe with no trial.
          </div>
        </div>
      </div>

      <div style={{ marginTop: 14, display: "flex", alignItems: "flex-start", gap: 12 }}>
        <Switch checked={sendEmail} onChange={setSendEmail} />
        <div>
          <div style={{ fontWeight: 500 }}>Email the invite</div>
          <div style={{ fontSize: 12, color: "#888" }}>
            Turn off to register on their behalf — the invite is still created and you get the
            link, but the customer isn't contacted.
          </div>
        </div>
      </div>
    </Modal>
  );
}
